/**
 * bootstrap-compat.ts
 * Lightweight drop-in replacement for bootstrap.bundle.min.js.
 * Exposes window.bootstrap.{ Modal, Toast, Tab, Dropdown, Carousel, Collapse }
 * and handles all data-bs-* attribute delegation so existing templates need no changes.
 */

/* ── Modal ──────────────────────────────────────────────────────────────── */
class BsModal {
  private el: HTMLElement
  private opts: { backdrop: boolean | 'static'; keyboard: boolean }
  private _backdrop: HTMLElement | null = null
  private _onKey: ((e: KeyboardEvent) => void) | null = null
  private _onBd: ((e: MouseEvent) => void) | null = null
  private static _map = new WeakMap<HTMLElement, BsModal>()

  constructor(el: HTMLElement, opts: Partial<{ backdrop: boolean | 'static'; keyboard: boolean }> = {}) {
    this.el = el
    this.opts = { backdrop: true, keyboard: true, ...opts }
    BsModal._map.set(el, this)
  }

  static getInstance(el: HTMLElement | null): BsModal | null {
    return el ? (BsModal._map.get(el) ?? null) : null
  }

  static getOrCreateInstance(el: HTMLElement, opts?: object): BsModal {
    return BsModal.getInstance(el) ?? new BsModal(el, opts)
  }

  show() {
    const el = this.el
    const bd = document.createElement('div')
    bd.className = 'modal-backdrop fade show'
    document.body.appendChild(bd)
    this._backdrop = bd
    el.style.display = 'block'
    el.removeAttribute('aria-hidden')
    el.setAttribute('aria-modal', 'true')
    el.setAttribute('role', 'dialog')
    document.body.classList.add('modal-open')
    document.body.style.overflow = 'hidden'
    requestAnimationFrame(() => el.classList.add('show'))

    if (this.opts.backdrop !== 'static') {
      this._onBd = (e: MouseEvent) => {
        if (e.target === el) this.hide()
      }
      el.addEventListener('mousedown', this._onBd)
    }

    if (this.opts.keyboard) {
      this._onKey = (e: KeyboardEvent) => {
        if (e.key === 'Escape') this.hide()
      }
      document.addEventListener('keydown', this._onKey)
    }

    el.dispatchEvent(new CustomEvent('shown.bs.modal'))
  }

  hide() {
    const el = this.el
    el.classList.remove('show')
    el.style.display = ''
    el.setAttribute('aria-hidden', 'true')
    el.removeAttribute('aria-modal')
    document.body.classList.remove('modal-open')
    document.body.style.overflow = ''
    if (this._backdrop) { this._backdrop.remove(); this._backdrop = null }
    if (this._onKey) { document.removeEventListener('keydown', this._onKey); this._onKey = null }
    if (this._onBd) { el.removeEventListener('mousedown', this._onBd); this._onBd = null }
    el.dispatchEvent(new CustomEvent('hidden.bs.modal'))
  }

  toggle() {
    this.el.classList.contains('show') ? this.hide() : this.show()
  }

  dispose() {
    BsModal._map.delete(this.el)
  }
}

/* ── Toast ──────────────────────────────────────────────────────────────── */
class BsToast {
  private el: HTMLElement
  private opts: { delay: number; autohide: boolean }
  private _timer: ReturnType<typeof setTimeout> | null = null

  constructor(el: HTMLElement, opts: Partial<{ delay: number; autohide: boolean }> = {}) {
    this.el = el
    this.opts = { delay: 5000, autohide: true, ...opts }
  }

  show() {
    this.el.classList.remove('hide')
    this.el.classList.add('show')
    if (this.opts.autohide) {
      if (this._timer) clearTimeout(this._timer)
      this._timer = setTimeout(() => this.hide(), this.opts.delay)
    }
  }

  hide() {
    this.el.classList.remove('show')
    this.el.classList.add('hide')
    if (this._timer) { clearTimeout(this._timer); this._timer = null }
  }

  dispose() {
    if (this._timer) clearTimeout(this._timer)
  }
}

/* ── Tab ────────────────────────────────────────────────────────────────── */
class BsTab {
  private el: HTMLElement

  constructor(el: HTMLElement) { this.el = el }

  show() {
    const targetSel = (this.el as HTMLElement).dataset.bsTarget
      || (this.el as HTMLAnchorElement).getAttribute('href')
    if (!targetSel) return

    const navParent = this.el.closest('.nav, [role="tablist"]') as HTMLElement | null
    if (navParent) {
      navParent.querySelectorAll<HTMLElement>('[data-bs-toggle="tab"],[data-bs-toggle="pill"]')
        .forEach(t => { t.classList.remove('active'); t.setAttribute('aria-selected', 'false') })
    }
    this.el.classList.add('active')
    this.el.setAttribute('aria-selected', 'true')

    const target = document.querySelector<HTMLElement>(targetSel)
    if (!target) return
    const paneParent = target.parentElement
    if (paneParent) {
      paneParent.querySelectorAll<HTMLElement>('.tab-pane')
        .forEach(p => p.classList.remove('show', 'active'))
    }
    target.classList.add('show', 'active')
  }
}

/* ── Dropdown ───────────────────────────────────────────────────────────── */
class BsDropdown {
  private el: HTMLElement

  constructor(el: HTMLElement) { this.el = el }

  show() {
    const menu = this.el.parentElement?.querySelector<HTMLElement>('.dropdown-menu')
    if (!menu) return
    menu.classList.add('show')
    this.el.setAttribute('aria-expanded', 'true')
    this._addOutsideListener(menu)
  }

  hide() {
    const menu = this.el.parentElement?.querySelector<HTMLElement>('.dropdown-menu')
    if (!menu) return
    menu.classList.remove('show')
    this.el.setAttribute('aria-expanded', 'false')
  }

  toggle() {
    const menu = this.el.parentElement?.querySelector<HTMLElement>('.dropdown-menu')
    if (menu?.classList.contains('show')) this.hide()
    else this.show()
  }

  private _addOutsideListener(menu: HTMLElement) {
    const parent = this.el.parentElement!
    const onOutside = (ev: MouseEvent) => {
      if (!parent.contains(ev.target as Node)) {
        menu.classList.remove('show')
        this.el.setAttribute('aria-expanded', 'false')
        document.removeEventListener('click', onOutside, true)
      }
    }
    setTimeout(() => document.addEventListener('click', onOutside, true), 0)
  }
}

/* ── Collapse ───────────────────────────────────────────────────────────── */
class BsCollapse {
  private el: HTMLElement

  constructor(el: HTMLElement) { this.el = el }

  show() {
    this.el.classList.add('show')
    this.el.dispatchEvent(new CustomEvent('shown.bs.collapse'))
  }

  hide() {
    this.el.classList.remove('show')
    this.el.dispatchEvent(new CustomEvent('hidden.bs.collapse'))
  }

  toggle() {
    this.el.classList.contains('show') ? this.hide() : this.show()
  }
}

/* ── Carousel ───────────────────────────────────────────────────────────── */
class BsCarousel {
  private el: HTMLElement
  private items: HTMLElement[] = []
  private current = 0
  private _timer: ReturnType<typeof setInterval> | null = null
  private static _map = new WeakMap<HTMLElement, BsCarousel>()

  constructor(el: HTMLElement, opts: Partial<{ interval: number | false }> = {}) {
    this.el = el
    this.items = Array.from(el.querySelectorAll<HTMLElement>('.carousel-item'))
    BsCarousel._map.set(el, this)

    // Set initial active
    if (this.items.length && !this.items.some(i => i.classList.contains('active'))) {
      this.items[0].classList.add('active')
    }
    this.current = Math.max(0, this.items.findIndex(i => i.classList.contains('active')))
    this._updateIndicators()

    const rawInterval = opts.interval !== undefined
      ? opts.interval
      : (parseInt(el.dataset.bsInterval || '') || 5000)
    if (rawInterval !== false && rawInterval > 0) {
      this._timer = setInterval(() => this.next(), rawInterval)
    }
  }

  static getInstance(el: HTMLElement | null): BsCarousel | null {
    return el ? (BsCarousel._map.get(el) ?? null) : null
  }

  static getOrCreateInstance(el: HTMLElement): BsCarousel {
    return BsCarousel.getInstance(el) ?? new BsCarousel(el)
  }

  private _go(index: number) {
    if (this.items.length === 0) return
    const next = ((index % this.items.length) + this.items.length) % this.items.length
    this.items.forEach((item, i) => item.classList.toggle('active', i === next))
    this.current = next
    this._updateIndicators()
    this.el.dispatchEvent(new CustomEvent('slid.bs.carousel', { detail: { to: next } }))
  }

  private _updateIndicators() {
    this.el.querySelectorAll('[data-bs-slide-to]').forEach((ind, i) => {
      ind.classList.toggle('active', i === this.current)
    })
  }

  next() { this._go(this.current + 1) }
  prev() { this._go(this.current - 1) }
  to(index: number) { this._go(index) }

  pause() { if (this._timer) { clearInterval(this._timer); this._timer = null } }

  cycle() {
    this.pause()
    const interval = parseInt(this.el.dataset.bsInterval || '') || 5000
    this._timer = setInterval(() => this.next(), interval)
  }

  dispose() {
    this.pause()
    BsCarousel._map.delete(this.el)
  }
}

/* ── Global event delegation ────────────────────────────────────────────── */

/** Close any open dropdowns not inside `except` */
function closeDropdowns(except?: HTMLElement | null) {
  document.querySelectorAll<HTMLElement>('.dropdown-menu.show').forEach(menu => {
    if (except && except.contains(menu)) return
    menu.classList.remove('show')
    const toggle = menu.parentElement?.querySelector<HTMLElement>('[data-bs-toggle="dropdown"]')
    if (toggle) toggle.setAttribute('aria-expanded', 'false')
  })
}

document.addEventListener('click', (e) => {
  const target = e.target as HTMLElement
  const el = target.closest<HTMLElement>(
    '[data-bs-dismiss],[data-bs-toggle],[data-bs-slide],[data-bs-slide-to]'
  )

  // Close compat-managed dropdowns only when clicking outside any .dropdown wrapper.
  // Vue-managed dropdowns (no data-bs-toggle) handle their own outside-click logic.
  if (!target.closest('.dropdown')) {
    closeDropdowns()
  }

  // Close compat-managed dropdowns when a dropdown-item is clicked
  if (target.closest('.dropdown-item') && !target.closest('[data-bs-toggle="dropdown"]')) {
    setTimeout(() => closeDropdowns(), 0)
  }

  if (!el) return

  const dismiss = el.dataset.bsDismiss
  const toggle  = el.dataset.bsToggle
  const slide   = el.dataset.bsSlide
  const slideTo = el.dataset.bsSlideTo

  /* dismiss –– modal */
  if (dismiss === 'modal') {
    const modalEl = el.closest<HTMLElement>('.modal')
    if (modalEl) (BsModal.getInstance(modalEl) ?? new BsModal(modalEl)).hide()
    return
  }

  /* dismiss –– alert */
  if (dismiss === 'alert') {
    el.closest('.alert')?.remove()
    return
  }

  /* dismiss –– toast */
  if (dismiss === 'toast') {
    const toastEl = el.closest<HTMLElement>('.toast')
    if (toastEl) new BsToast(toastEl).hide()
    return
  }

  /* toggle –– modal */
  if (toggle === 'modal') {
    const sel = el.dataset.bsTarget
    if (!sel) return
    const modalEl = document.querySelector<HTMLElement>(sel)
    if (modalEl) (BsModal.getInstance(modalEl) ?? new BsModal(modalEl)).show()
    return
  }

  /* toggle –– dropdown */
  if (toggle === 'dropdown') {
    e.stopPropagation()
    new BsDropdown(el).toggle()
    return
  }

  /* toggle –– tab / pill */
  if (toggle === 'tab' || toggle === 'pill') {
    e.preventDefault()
    new BsTab(el).show()
    return
  }

  /* toggle –– collapse */
  if (toggle === 'collapse') {
    e.preventDefault()
    const sel = el.dataset.bsTarget || el.getAttribute('href')
    if (!sel) return
    const collapseEl = document.querySelector<HTMLElement>(sel)
    if (collapseEl) new BsCollapse(collapseEl).toggle()
    el.classList.toggle('collapsed', !collapseEl?.classList.contains('show'))
    el.setAttribute('aria-expanded', String(collapseEl?.classList.contains('show') ?? false))
    return
  }

  /* carousel –– prev / next */
  if (slide === 'prev' || slide === 'next') {
    const sel = el.dataset.bsTarget
    const carEl = sel ? document.querySelector<HTMLElement>(sel) : el.closest<HTMLElement>('.carousel')
    if (carEl) {
      const car = BsCarousel.getInstance(carEl) ?? new BsCarousel(carEl)
      slide === 'prev' ? car.prev() : car.next()
      car.pause()
    }
    return
  }

  /* carousel –– slide-to indicator */
  if (slideTo !== undefined) {
    const sel = el.dataset.bsTarget
      || el.closest<HTMLElement>('[data-bs-target]')?.dataset.bsTarget
    const carEl = sel ? document.querySelector<HTMLElement>(sel) : el.closest<HTMLElement>('.carousel')
    if (carEl) {
      const car = BsCarousel.getInstance(carEl) ?? new BsCarousel(carEl)
      car.to(parseInt(slideTo, 10))
      car.pause()
    }
    return
  }
})

/* ── Auto-init carousels (MutationObserver for Vue-rendered content) ─────── */
function initCarousel(el: HTMLElement) {
  if (!BsCarousel.getInstance(el)) new BsCarousel(el)
}

function scanCarousels(root: HTMLElement | Document = document) {
  root.querySelectorAll<HTMLElement>('.carousel[data-bs-ride="carousel"]')
    .forEach(initCarousel)
}

const _mo = new MutationObserver((mutations) => {
  for (const m of mutations) {
    for (const node of m.addedNodes) {
      if (node.nodeType !== 1) continue
      const el = node as HTMLElement
      if (el.matches?.('.carousel[data-bs-ride="carousel"]')) initCarousel(el)
      scanCarousels(el)
    }
  }
})

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', () => {
    scanCarousels()
    _mo.observe(document.body, { childList: true, subtree: true })
  })
} else {
  scanCarousels()
  _mo.observe(document.body, { childList: true, subtree: true })
}

/* ── Expose as window.bootstrap ─────────────────────────────────────────── */
;(window as any).bootstrap = {
  Modal:    BsModal,
  Toast:    BsToast,
  Tab:      BsTab,
  Dropdown: BsDropdown,
  Collapse: BsCollapse,
  Carousel: BsCarousel,
}

export {}
